import { useState, type FormEvent } from 'react'
import { AvisoError, Campo, CLASE_BOTON, CLASE_BOTON_PRIMARIO, CLASE_INPUT } from '../../componentes/formulario'
import { DIAS, SEMANAS } from '../agendamiento/tabla'
import { validarCliente, type ValoresCliente } from './formCliente'

interface Props {
  inicial: ValoresCliente
  rotuloGuardar: string
  guardando: boolean
  error: unknown
  onGuardar: (valores: ValoresCliente) => void
  onCancelar?: () => void
}

/** Datos, etiquetas de reparto y coordenadas de un cliente (RF-40). */
export function FormCliente({ inicial, rotuloGuardar, guardando, error, onGuardar, onCancelar }: Props) {
  const [v, setV] = useState<ValoresCliente>(inicial)
  const [errores, setErrores] = useState<string[]>([])
  const poner = <K extends keyof ValoresCliente>(campo: K, valor: ValoresCliente[K]) => setV((a) => ({ ...a, [campo]: valor }))
  const texto = (campo: keyof ValoresCliente) => ({
    value: String(v[campo]),
    onChange: (e: { target: { value: string } }) => poner(campo, e.target.value as never),
    className: CLASE_INPUT,
  })

  const enviar = (e: FormEvent) => {
    e.preventDefault()
    const encontrados = validarCliente(v)
    setErrores(encontrados)
    if (encontrados.length === 0) onGuardar(v)
  }

  return (
    <form onSubmit={enviar} className="flex flex-col gap-4" noValidate>
      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="mb-2 text-sm font-semibold text-tinta">Datos</legend>
        <Campo etiqueta="Nombre *"><input {...texto('nombre')} required /></Campo>
        <Campo etiqueta="NIF"><input {...texto('nif')} /></Campo>
        <Campo etiqueta="Móvil"><input {...texto('movil')} inputMode="tel" /></Campo>
        <Campo etiqueta="Teléfono"><input {...texto('telefono')} inputMode="tel" /></Campo>
        <Campo etiqueta="Correo"><input {...texto('email')} type="email" /></Campo>
        <Campo etiqueta="Dirección"><input {...texto('direccion')} /></Campo>
        <Campo etiqueta="Código postal"><input {...texto('codigo_postal')} /></Campo>
        <Campo etiqueta="Ciudad"><input {...texto('ciudad')} /></Campo>
        <Campo etiqueta="Provincia"><input {...texto('provincia')} /></Campo>
        <Campo etiqueta="Notas"><textarea {...texto('notas')} rows={2} /></Campo>
      </fieldset>

      <fieldset className="grid gap-3 sm:grid-cols-3">
        <legend className="mb-2 text-sm font-semibold text-tinta">Etiquetas de reparto</legend>
        <Campo etiqueta="Ruta (día de reparto)">
          <select value={v.ruta} onChange={(e) => poner('ruta', e.target.value as ValoresCliente['ruta'])} className={CLASE_INPUT}>
            <option value="">Sin ruta</option>
            {DIAS.map((d) => (
              <option key={d.clave} value={d.clave}>{`${d.etiqueta} (${d.clave.toUpperCase()})`}</option>
            ))}
          </select>
        </Campo>
        <Campo etiqueta="Semana del ciclo">
          <select value={v.semana} onChange={(e) => poner('semana', e.target.value as ValoresCliente['semana'])} className={CLASE_INPUT}>
            <option value="">Sin semana</option>
            {SEMANAS.map((s) => (
              <option key={s.clave} value={s.clave}>{s.etiqueta}</option>
            ))}
          </select>
        </Campo>
        <label className="flex items-center gap-2 self-end pb-2 text-sm text-tinta-2">
          <input type="checkbox" checked={v.granEmpresa} onChange={(e) => poner('granEmpresa', e.target.checked)} />
          Gran empresa
        </label>
      </fieldset>

      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="mb-2 text-sm font-semibold text-tinta">Ubicación</legend>
        <Campo etiqueta="Latitud" ayuda="Entre -90 y 90, p. ej. 39.98640">
          <input {...texto('latitud')} inputMode="decimal" />
        </Campo>
        <Campo etiqueta="Longitud" ayuda="Entre -180 y 180, p. ej. -0.05130">
          <input {...texto('longitud')} inputMode="decimal" />
        </Campo>
      </fieldset>

      {errores.length > 0 ? (
        <ul role="alert" className="list-disc rounded-lg border border-atencion-anillo bg-atencion-fondo py-2 pl-8 pr-3 text-sm text-atencion-tinta">
          {errores.map((m) => <li key={m}>{m}</li>)}
        </ul>
      ) : null}
      <AvisoError error={error} />

      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={guardando} className={CLASE_BOTON_PRIMARIO}>
          {guardando ? 'Guardando' : rotuloGuardar}
        </button>
        {onCancelar ? (
          <button type="button" onClick={onCancelar} className={CLASE_BOTON}>Cancelar</button>
        ) : null}
      </div>
    </form>
  )
}
